import { captureException } from "@sentry/react";
import { msalPublicClientApplication } from "../../core/auth/useAuth";
import { tokenScopes } from "@coworker/apprestructured/src/assets/auth/msalConfig";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { reportMessageToSentry } from "../useConfigureSentry";

const getMsalToken = async () => {
  const msalInstance = await msalPublicClientApplication;
  const accounts = msalInstance.getAllAccounts();

  const isCacheCorruptionError = (msg: string) =>
    msg.includes("Unexpected token") ||
    msg.includes("JSON") ||
    msg.includes("cache") ||
    msg.includes("parsing") ||
    msg.includes("unable_to_parse") ||
    msg.includes("crypto_key_not_found") ||
    msg.includes("key_not_found");

  if (!accounts[0]) {
    try {
      await msalInstance.acquireTokenRedirect({
        scopes: tokenScopes,
      });
      return null;
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "interaction_in_progress"
      ) {
        return null;
      } else {
        reportMessageToSentry(
          `Error during acquireTokenRedirect.`,
          { accounts },
          "warning"
        );
        throw error;
      }
    }
  } else {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: tokenScopes,
        account: accounts[0] as AccountInfo,
      });
      sessionStorage.removeItem("msal.redirected");
      return response.accessToken;
    } catch (error: any) {
      const message = error?.message || "";

      // Check and log to sentry if MSAL cache corruption, no action required atm.
      if (isCacheCorruptionError(message)) {
        reportMessageToSentry(
          `MSAL cache corruption detected`,
          { accounts, error: message },
          "error"
        );
        // Just return null for now, no force redirect
        return null;
      }

      // Only log refresh token expiry
      if (
        message.includes("AADSTS70008") ||
        message.includes("refresh token has expired") ||
        message.includes("inactivity")
      ) {
        reportMessageToSentry(
          `MSAL refresh token expired due to inactivity`,
          { accounts, error: message },
          "error"
        );
        // User can manually login
        return null;
      }

      // Only do interactive login if explicitly required
      if (error instanceof InteractionRequiredAuthError) {
        if (sessionStorage.getItem("msal.redirected") === "1") {
          return null;
        }
        sessionStorage.setItem("msal.redirected", "1");
        try {
          await msalInstance.acquireTokenRedirect({
            scopes: tokenScopes,
          });
          return null;
        } catch (loginError) {
          reportMessageToSentry(
            `Catch block, error during InteractionRequiredAuthError`,
            { accounts: accounts[0], error: loginError },
            "warning"
          );
          return null;
        }
      } else {
        captureException(error, {
          tags: { area: "auth", flow: "token_acquisition" },
          extra: { accounts },
        });
        return null;
      }
    }
  }
};

export async function getTokenAsync() {
  return await getMsalToken();
}
